import { Observable, BehaviorSubject, of as observableOf, of } from 'rxjs';
import { mergeMap, catchError, share, delay, filter, take } from "rxjs/operators";
import { IBaseResponse } from '../ClientService';
import { addProvider, IProviderDescriptor } from "../ServiceUtil";
import {
    IEmployee,
    IEmployeeAddress,
    INewEmployee,
    INewEmployeeAddress,
    INewEmployeeDocument,
    INewEmploymentHistory,
    IEmploymentHistory,
    IEmployeeResponse,
    IUpdateEmployeeDocument,
    INewPayrollRate,
    IPayrollRate,
    IEmployeeInsurance,
    IOnboardEmployee,
    IEmployeeEmploymentStatus,
    INewEmployeeEmploymentStatus,
    IEmployeeAccount,
    INewEmployeeAccount,
    INewEmployeeBulk,
    IEmployeeEmergencyContact,
    INewEmployeeEmergencyContact,
    IEmployeeEducationHistory,
    INewEmployeeEducationHistory,
    IOrgTreeNode,
    IManagerEmployee,
    IJoiningFormInvite,
    INewJoiningFormInvite,
    IJoiningFormInviteResponse,
    INewJoiningFormSubmission,
    IJoiningFormSubmitResponse,
    IJoiningFormDocumentResponse,
} from "./employee.interface";
import { EmployeeDelegate } from "./employee.delegate";
import { AuthenticationService } from "../AuthenticationService";
import { InitializationModel, InitializationStatusCodes } from '../Initialization';
import { IEmployeeCheck } from '../payroll';

export class EmployeeService {
    private employeessData: IEmployee[] = null;
    private employeesSubject: BehaviorSubject<IEmployee[]> = new BehaviorSubject(null);
    public employees: Observable<IEmployee[]> = this.employeesSubject;

    private allEmployeesSubject: BehaviorSubject<IEmployee[]> = new BehaviorSubject(null);
    public allEmployees: Observable<IEmployee[]> = this.allEmployeesSubject;

    private employeesFilteredSubject: BehaviorSubject<IEmployee[]> = new BehaviorSubject(null);
    public employeesFiltered: Observable<IEmployee[]> = this.employeesFilteredSubject;

    public filterEmployeStatus: boolean = true;

    private selectedEmployeeId: string = "0";

    private selectedEmployeeData: IEmployee = null;
    private selectedEmployeeSubject: BehaviorSubject<IEmployee> = new BehaviorSubject(this.selectedEmployeeData);
    public selectedEmployee: Observable<IEmployee> = this.selectedEmployeeSubject;


    private onboardEmployeesData: IOnboardEmployee[] = null;
    private onboardEmployeesSubject: BehaviorSubject<IOnboardEmployee[]> = new BehaviorSubject(null);
    public onboardEmployees: Observable<IOnboardEmployee[]> = this.onboardEmployeesSubject;

    private onboardEmployeesFilteredSubject: BehaviorSubject<IOnboardEmployee[]> = new BehaviorSubject(null);
    public onboardEmployeesFiltered: Observable<IOnboardEmployee[]> = this.onboardEmployeesFilteredSubject.pipe(filter((data) => !!data));

    private selectedOnboardId: string = '0';

    private selectedOnboardEmployeeData: IOnboardEmployee = null;
    private selectedOnboardEmployeeSubject: BehaviorSubject<IOnboardEmployee> = new BehaviorSubject(this.selectedOnboardEmployeeData);
    public selectedOnboardEmployee: Observable<IOnboardEmployee> = this.selectedOnboardEmployeeSubject.pipe(filter((data) => !!data));


    private orgTreeData: IOrgTreeNode[] = null;
    private orgTreeSubject: BehaviorSubject<IOrgTreeNode[]> = new BehaviorSubject(null);
    public orgTree: Observable<IOrgTreeNode[]> = this.orgTreeSubject;

    private managerListData: IManagerEmployee[] = null;
    private managerListSubject: BehaviorSubject<IManagerEmployee[]> = new BehaviorSubject(null);
    public managerEmployees: Observable<IManagerEmployee[]> = this.managerListSubject;


    private joiningFormInvitesData: IJoiningFormInvite[] = null;
    private joiningFormInvitesSubject: BehaviorSubject<IJoiningFormInvite[]> = new BehaviorSubject(null);
    public joiningFormInvites: Observable<IJoiningFormInvite[]> = this.joiningFormInvitesSubject;

    private joiningFormInvitesFilteredSubject: BehaviorSubject<IJoiningFormInvite[]> = new BehaviorSubject<IJoiningFormInvite[]>([]);
    public joiningFormInvitesFiltered: Observable<IJoiningFormInvite[]> = this.joiningFormInvitesFilteredSubject.asObservable();

    private subordinateEmployeesData: IOrgTreeNode[] = null;
    private subordinateEmployeesSubject: BehaviorSubject<IOrgTreeNode[]> = new BehaviorSubject(null);
    public subordinateEmployeesFlat: Observable<IOrgTreeNode[]> = this.subordinateEmployeesSubject;

    constructor(private employeeDelegate: EmployeeDelegate,
        private authenticationService: AuthenticationService,
        private initService: InitializationModel) {
        this.initService.initializationState$
            .pipe(
                filter(state => state === InitializationStatusCodes.RUNNING),
                take(1)
            )
            .subscribe(() => {
                // Only make API calls if user is still authenticated and authorized
                const session = this.authenticationService.session;
                if (session && session.loggedin && session.authenticated && authenticationService.makeAllCalls()) {
                    console.log('EmployeeService: User authenticated and authorized, loading employee data');
                    this.getAllEmployees();
                }
                else {
                    console.log('EmployeeService: User not authenticated or not authorized, skipping employee data load');
                }
            });
    }

    public get employeeId(): string {
        return this.selectedEmployeeId;
    }

    public setSelectedEmployeeId(employeeId: string): void {
        this.selectedEmployeeId = employeeId;
    }

    public get onboardEmployeeId(): string {
        return this.selectedOnboardId;
    }

    public setSelectedOnboardingEmployeeId(onboardEmployeeId: string): void {
        this.selectedOnboardId = onboardEmployeeId;
    }

    public getAllEmployees(hardReload: boolean = false): Observable<boolean> {
        if (this.employeessData !== null && this.employeessData.length > 0 && hardReload === false) {
            return observableOf(true);
        }
        const orgId = this.authenticationService.GetOrgId();
        const response = this.employeeDelegate.getAllEmployeesByOrgId(orgId);

        return this.processEmployees(response);
    }

    public getAllOnboardEmployees(hardReload: boolean = false): Observable<boolean> {
        if (this.onboardEmployeesData !== null && this.onboardEmployeesData.length > 0 && hardReload === false) {
            return observableOf(true);
        }
        const orgId = this.authenticationService.GetOrgId();
        const userId = this.authenticationService.GetUserId();
        const response = this.employeeDelegate.getAllOnboardEmployeesByOrgId(orgId, userId);
        return this.processOnboardEmployees(response);
    }

    public getOnboardEmployeeDetailsById(onboardEmployeeId: string): Observable<boolean> {
        if (onboardEmployeeId === "0") {
            this.selectedOnboardEmployeeData = null;
            this.selectedOnboardEmployeeSubject.next(this.selectedOnboardEmployeeData);
            return observableOf(false);
        }
        const userId = this.authenticationService.GetUserId();

        var response = this.employeeDelegate.getOnboardEmployeesByEmployeeId(onboardEmployeeId, userId);

        return this.processOnboardEmployee(response);
    }

    public createNewEmployeeBulk(benchLead: INewEmployeeBulk): Observable<boolean> {
        const response = this.employeeDelegate.createNewEmployeeBulk({
            ...benchLead,
            ...this.authenticationService.getBaseRequest
        });
        return this.processEmployees(response);
    }


    public getEmployeeDetailsById(employeeId: string): Observable<boolean> {
        if (employeeId === "0") {
            this.selectedEmployeeData = null;
            this.selectedEmployeeSubject.next(this.selectedEmployeeData);
            return observableOf(false);
        }
        var response = this.employeeDelegate.getEmployeeEmployeeId(employeeId);

        return this.processEmployee(response);
    }

    public getTimeSheetSpecsById(employeeId: string): Observable<boolean> {
        if (employeeId === "0") {
            this.selectedEmployeeData = null;
            this.selectedEmployeeSubject.next(this.selectedEmployeeData);
            return observableOf(false);
        }
        var response = this.employeeDelegate.getTimeSheetSpecsEmployeeId(employeeId);

        return this.processEmployee(response);
    }

    public createNewEmployee(employee: INewEmployee): Observable<boolean> {
        const response = this.employeeDelegate.createNewEmployee({ ...employee, ...this.authenticationService.getBaseRequest });
        return this.processEmployees(response);
    }

    public updateEmployee(employee: IEmployee): Observable<boolean> {
        var response = this.employeeDelegate.updateEmployee({ ...employee, ...this.authenticationService.getBaseRequest });

        return this.processEmployee(response);
    }

    public updateOnboardEmployee(employee: IOnboardEmployee): Observable<boolean> {
        var response = this.employeeDelegate.updateOnboardEmployee({ ...employee, ...this.authenticationService.getBaseRequest });

        return this.processOnboardEmployee(response);
    }

    public updateEmployeeInsurance(employee: IEmployeeInsurance): Observable<boolean> {
        var response = this.employeeDelegate.updateEmployeeInsurance({ ...employee, ...this.authenticationService.getBaseRequest });

        return response.pipe(mergeMap((response: IEmployeeResponse) => observableOf(response.success)),
            catchError(() => observableOf(false)),
            share());
    }

    public newEmployeeInsurance(employee: IEmployeeInsurance): Observable<boolean> {
        var response = this.employeeDelegate.createNewEmployeeInsurance({ ...employee, ...this.authenticationService.getBaseRequest });

        return response.pipe(mergeMap((response: IEmployeeResponse) => observableOf(response.success)),
            catchError(() => observableOf(false)),
            share());
    }


    public createNewAddress(employeeAddress: IEmployeeAddress, employeeId: string): Observable<boolean> {
        const newAddress: INewEmployeeAddress = {
            ...employeeAddress,
            employeeId: employeeId,
            ...this.authenticationService.getBaseRequest
        };

        const response = this.employeeDelegate.createEmployeeAddress(newAddress);
        return this.processEmployee(response);
    }

    public createNewAccount(employeeAddress: IEmployeeAccount, employeeId: string): Observable<boolean> {
        const newAddress: INewEmployeeAccount = {
            ...employeeAddress,
            employeeId: employeeId,
            ...this.authenticationService.getBaseRequest
        };

        const response = this.employeeDelegate.createEmployeeAccount(newAddress);
        return this.processEmployee(response);
    }

    public updateNewAccount(employeeAccount: IEmployeeAccount): Observable<boolean> {
        const response = this.employeeDelegate.updateEmployeeAccount({
            ...employeeAccount,
            ...this.authenticationService.getBaseRequest
        });
        return this.processEmployee(response);
    }

    public createNewEmploymentStatus(emplomentStatus: IEmployeeEmploymentStatus, employeeId: string): Observable<boolean> {
        const newAddress: INewEmployeeEmploymentStatus = {
            ...emplomentStatus,
            employeeId: employeeId,
            ...this.authenticationService.getBaseRequest
        };

        const response = this.employeeDelegate.createEmployeeEmploymentStatus(newAddress);
        return this.processEmployee(response);
    }

    public createNewPayRate(employeePayrate: INewPayrollRate, employeeId: string): Observable<boolean> {
        const newAddress: INewPayrollRate = {
            ...employeePayrate,
            employeeId: employeeId,
            ...this.authenticationService.getBaseRequest
        };

        const response = this.employeeDelegate.createEmployeePayRate({
            ...newAddress,
            ...this.authenticationService.getBaseRequest
        });
        return this.processEmployee(response);
    }

    public updateEmploymentStatus(emplomentStatus: IEmployeeEmploymentStatus): Observable<boolean> {
        var response = this.employeeDelegate.updateEmployeeEmploymentStatus({
            ...emplomentStatus,
            ...this.authenticationService.getBaseRequest
        });

        return this.processEmployee(response);
    }

    public updateAddress(employeeAddress: IEmployeeAddress): Observable<boolean> {
        var response = this.employeeDelegate.updateEmployeeAddress({
            ...employeeAddress,
            ...this.authenticationService.getBaseRequest
        });

        return this.processEmployee(response);
    }

    public updatePayRate(employeeAddress: IPayrollRate): Observable<boolean> {
        var response = this.employeeDelegate.updateEmployeePayRate({
            ...employeeAddress,
            ...this.authenticationService.getBaseRequest
        });

        return this.processEmployee(response);
    }


    public createNewEmployeeDocuments(employeeDocuments: INewEmployeeDocument): Observable<boolean> {
        const response = this.employeeDelegate.createEmployeeDocuments({
            ...employeeDocuments,
            ...this.authenticationService.getBaseRequest
        });
        return this.processEmployee(response);
    }

    public updateEmployeeDocuments(employeeDocuments: IUpdateEmployeeDocument): Observable<boolean> {
        const response = this.employeeDelegate.updateEmployeeDocuments({
            ...employeeDocuments,
            ...this.authenticationService.getBaseRequest
        });
        return this.processEmployee(response);
    }

    public createNewEmployeeHistory(employeeHistory: INewEmploymentHistory): Observable<boolean> {
        const response = this.employeeDelegate.createEmployeeHistory({
            ...employeeHistory,
            ...this.authenticationService.getBaseRequest
        });
        return this.processEmployee(response);
    }

    public updateEmployeeHistory(employeeHistory: IEmploymentHistory): Observable<boolean> {
        const response = this.employeeDelegate.updateEmployeeHistory({
            ...employeeHistory,
            ...this.authenticationService.getBaseRequest
        });
        return this.processEmployee(response);
    }

    public createEmergencyContact(contact: INewEmployeeEmergencyContact): Observable<boolean> {
        const response = this.employeeDelegate.createEmergencyContact({
            ...contact,
            ...this.authenticationService.getBaseRequest
        });
        return this.processEmployee(response);
    }

    public updateEmergencyContact(contact: IEmployeeEmergencyContact): Observable<boolean> {
        const response = this.employeeDelegate.updateEmergencyContact({
            ...contact,
            ...this.authenticationService.getBaseRequest
        });
        return this.processEmployee(response);
    }

    public createEducationHistory(education: INewEmployeeEducationHistory): Observable<boolean> {
        const response = this.employeeDelegate.createEducationHistory({
            ...education,
            ...this.authenticationService.getBaseRequest
        });
        return this.processEmployee(response);
    }

    public updateEducationHistory(education: IEmployeeEducationHistory): Observable<boolean> {
        const response = this.employeeDelegate.updateEducationHistory({
            ...education,
            ...this.authenticationService.getBaseRequest
        });
        return this.processEmployee(response);
    }

    public getOrgReportingTree(): Observable<boolean> {
        const orgId = this.authenticationService.GetOrgId();
        const response = this.employeeDelegate.getOrgReportingTree(orgId);

        response.subscribe(
            (res: IEmployeeResponse) => {
                if (res.success && res.tree) {
                    this.orgTreeData = res.tree;
                    this.orgTreeSubject.next(this.orgTreeData);
                }
            },
            () => { }
        );

        return response.pipe(
            mergeMap((res: IEmployeeResponse) => observableOf(res.success)),
            catchError(() => observableOf(false)),
            share()
        );
    }

    public getSubordinateTree(managerEmployeeId: string): Observable<boolean> {
        const response = this.employeeDelegate.getSubordinateTree(managerEmployeeId);

        response.subscribe(
            (res: IEmployeeResponse) => {
                if (res.success && res.tree) {
                    this.orgTreeSubject.next(res.tree);
                }
            },
            () => { }
        );

        return response.pipe(
            mergeMap((res: IEmployeeResponse) => observableOf(res.success)),
            catchError(() => observableOf(false)),
            share()
        );
    }

    public getSubordinateTrees(managerEmployeeId: string): Observable<boolean> {
        if ( this.subordinateEmployeesData !== null &&this.subordinateEmployeesData.length > 0 ) {
            return observableOf(true);
        }
        const response = this.employeeDelegate.getSubordinateTree(managerEmployeeId);

        response.subscribe(
            (res: IEmployeeResponse) => {
                if (res.success && res.tree) {
                    this.orgTreeSubject.next(res.tree);
                    this.subordinateEmployeesData = EmployeeDelegate.flattenTree(res.tree);
                    this.subordinateEmployeesSubject.next(this.subordinateEmployeesData);
                }
            },
            () => { }
        );

        return response.pipe(
            mergeMap((res: IEmployeeResponse) => observableOf(res.success)),
            catchError(() => observableOf(false)),
            share()
        );
    }

    public getEmployeesForProjects(active: boolean): Observable<IEmployee[]> {
        return observableOf(this.employeessData.filter(x => x.active === active || x.futureEmployee === true));
    }

    public getEmpoyeesByStatus(active: boolean) {
        this.filterEmployeStatus = active;
        let data = this.employeessData;
        data = this.employeessData.filter(x => x.active === active);
        this.employeesFilteredSubject.next(data);
    }

    public getEmpoyeesFilter(active: boolean, filterName: string) {
        this.filterEmployeStatus = active;
        let data = this.employeessData;
        switch (filterName) {
            case "true": {
                data = this.employeessData.filter(x => x.futureEmployee === true);
                break;
            }
            case "false": {
                data = this.employeessData.filter(x => x.active === active && x.createEmployeeLogin === false);
                break;
            }
        }

        this.employeesFilteredSubject.next(data);
    }

    public getEmployeeTypeFilter(active: boolean, employeeTypeId: string) {
        this.filterEmployeStatus = active;
        let data = this.employeessData;


        data = this.employeessData.filter(x => x.employeeTypeId === employeeTypeId);
        this.employeesFilteredSubject.next(data);
    }

    public filterEmployees(searchTerm: string, filterBy: string) {
        let data = this.employeessData;
        if (filterBy == null) {
            data = this.employeessData.filter(x =>
                x.firstName.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
                || x.emailAddress.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
                || x.mobile.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
                || x.payrollId.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
        }
        else {
            data = this.employeessData.filter(x =>
                x[filterBy].trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
        }

        this.employeesFilteredSubject.next(data);
    }

    public setEmployeeDataInList() {
        let data = this.employeessData;
        const index = this.employeessData.findIndex(x => x.employeeId === this.selectedEmployeeData.employeeId);
        this.employeessData[index] = this.selectedEmployeeData;
        data = this.employeessData.filter(x => x.active === this.filterEmployeStatus);

        this.employeesFilteredSubject.next(data);
    }

    public filterOnboardEmployees(searchTerm: string, filterBy: string) {
        let data = this.onboardEmployeesData;
        if (filterBy == null) {
            data = this.onboardEmployeesData.filter(x =>
                x.lastName.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
                || x.firstName.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
        }
        else {
            data = this.onboardEmployeesData.filter(x =>
                x[filterBy].trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
        }

        this.onboardEmployeesFilteredSubject.next(data);
    }

    private processEmployee(response: Observable<IEmployeeResponse>): Observable<boolean> {
        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));

        return response.pipe(mergeMap((response: IEmployeeResponse) => observableOf(response.success)),
            catchError(() => observableOf(false)),
            share());

        function onEmployeeSuccess(employeResponse: IEmployeeResponse) {
            if (employeResponse.success && employeResponse.employee) {
                this.selectedEmployeeId = employeResponse.employeeId ? employeResponse.employeeId : this.selectedEmployeeId;
                this.selectedEmployeeData = employeResponse.employee;
                this.selectedEmployeeSubject.next(this.selectedEmployeeData);
                this.setEmployeeDataInList();
                this.refreshManagerList();
            }
        }

        function onEmployeeFault(error: any) {
        }
    }

    private processEmployees(response: Observable<IEmployeeResponse>): Observable<boolean> {
        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));

        return response.pipe(mergeMap((response: IEmployeeResponse) => observableOf(response.success)),
            catchError(() => observableOf(false)),
            share());

        function onEmployeeSuccess(employeResponse: IEmployeeResponse) {
            if (employeResponse.success && employeResponse.employees.length > 0) {
                this.selectedEmployeeId = employeResponse.employeeId ? employeResponse.employeeId : this.selectedEmployeeId;

                this.employeessData = employeResponse.employees;
                this.employeesSubject.next(this.employeessData.filter(x => x.active === true || x.futureEmployee === true));
                this.selectedEmployeeData = this.employeessData.find(x => x.employeeId === this.selectedEmployeeId);
                this.selectedEmployeeSubject.next(this.selectedEmployeeData);

                this.allEmployeesSubject.next(this.employeessData);
                this.getEmpoyeesByStatus(this.filterEmployeStatus);
                this.refreshManagerList();
            }
        }

        function onEmployeeFault(error: any) {
            //EmployeeService.logger.error(`processEmployees Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processOnboardEmployees(response: Observable<IEmployeeResponse>): Observable<boolean> {
        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));

        return response.pipe(mergeMap((response: IEmployeeResponse) => observableOf(response.success)),
            catchError(() => observableOf(false)),
            share());

        function onEmployeeSuccess(employeResponse: IEmployeeResponse) {
            if (employeResponse.success && employeResponse.onboardEmployees.length > 0) {
                this.selectedOnboardId = employeResponse.onboardEmployeeId ? employeResponse.onboardEmployeeId : this.selectedOnboardId;

                this.onboardEmployeesData = employeResponse.onboardEmployees;
                this.onboardEmployeesSubject.next(this.onboardEmployeesData);
                this.onboardEmployeesFilteredSubject.next(this.onboardEmployeesData);
            }
        }

        function onEmployeeFault(error: any) {
            //EmployeeService.logger.error(`processEmployees Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processOnboardEmployee(clients: Observable<IEmployeeResponse>): Observable<boolean> {
        clients.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));

        return clients.pipe(mergeMap((response: IEmployeeResponse) => observableOf(response.success)),
            catchError(() => observableOf(false)),
            share());

        function onEmployeeSuccess(employeResponse: IEmployeeResponse) {
            if (employeResponse.success) {
                this.selectedOnboardId = employeResponse.onboardEmployeeId ? employeResponse.onboardEmployeeId : this.selectedOnboardId;
                this.selectedOnboardEmployeeData = employeResponse.onboardEmployee;
                this.selectedOnboardEmployeeSubject.next(this.selectedOnboardEmployeeData);
            }
        }

        function onEmployeeFault(error: any) {
            //  ClientService.logger.error(`processClients Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private refreshManagerList(): void {
        if (!this.employeessData) { return; }
        this.managerListData = this.employeessData
            .filter(e => e.active === true && e.isManager === true)
            .map(e => ({
                employeeId: e.employeeId,
                employeeFullName: e.jobTitle
                    ? `${e.employeeFullName} � ${e.jobTitle}`
                    : (e.employeeFullName || ''),
                jobTitle: e.jobTitle
            }));
        this.managerListSubject.next(this.managerListData);
    }

    public getAllJoiningFormInvites(hardReload: boolean = false): Observable<boolean> {
        if (this.joiningFormInvitesData !== null && this.joiningFormInvitesData.length > 0 && hardReload === false) {
            this.joiningFormInvitesFilteredSubject.next(this.joiningFormInvitesData);
            return observableOf(true);
        }
        const orgId = this.authenticationService.GetOrgId();
        const response = this.employeeDelegate.getAllJoiningFormInvites(orgId);

        response.subscribe(
            (res: IJoiningFormInviteResponse) => {
                this.joiningFormInvitesData = res.invites || [];
                this.joiningFormInvitesSubject.next(this.joiningFormInvitesData);
                this.joiningFormInvitesFilteredSubject.next(this.joiningFormInvitesData);
            },
            () => { }
        );

        return response.pipe(
            mergeMap((res: IJoiningFormInviteResponse) => observableOf(res.success)),
            catchError(() => observableOf(false)),
            share()
        );
    }

    public sendJoiningFormInvite(invite: INewJoiningFormInvite): Observable<boolean> {
        const response = this.employeeDelegate.sendJoiningFormInvite(invite);

        return response.pipe(
            mergeMap((res: IJoiningFormInviteResponse) => {
                if (res.success) {
                    this.getAllJoiningFormInvites(true).subscribe();
                }
                return observableOf(res.success);
            }),
            catchError(() => observableOf(false)),
            share()
        );
    }

    public filterJoiningFormInvites(searchTerm: string, filterBy: string = null): void {
        const data = this.joiningFormInvitesData || [];
        if (!searchTerm || searchTerm.trim() === '') {
            this.joiningFormInvitesFilteredSubject.next(data);
            return;
        }
        const term = searchTerm.toLowerCase();
        const filtered = data.filter(invite => {
            if (filterBy) {
                return invite[filterBy]?.toString().toLowerCase().includes(term);
            }
            return (
                invite.firstName?.toLowerCase().includes(term) ||
                invite.lastName?.toLowerCase().includes(term) ||
                invite.email?.toLowerCase().includes(term) ||
                invite.workflowStatus?.toLowerCase().includes(term)
            );
        });
        this.joiningFormInvitesFilteredSubject.next(filtered);
    }

    public validateJoiningFormToken(token: string): Observable<IJoiningFormInviteResponse> {
        return this.employeeDelegate.validateJoiningFormToken(token).pipe(
            catchError(() => observableOf({ success: false, errorString: 'Network error' })),
            share()
        );
    }

    public submitJoiningForm(token: string, submission: INewJoiningFormSubmission): Observable<IJoiningFormSubmitResponse> {
        return this.employeeDelegate.submitJoiningForm(token, submission).pipe(
            catchError(() => observableOf({ success: false, errorString: 'Network error' })),
            share()
        );
    }

    public uploadJoiningFormDocuments(token: string, files: File[], documentTypes: string[]): Observable<IJoiningFormDocumentResponse> {
        return this.employeeDelegate.uploadJoiningFormDocuments(token, files, documentTypes).pipe(
            catchError(() => observableOf({ success: false, documents: [] })),
            share()
        );
    } 
    
}

